import { Fragment, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDispatch } from 'react-redux'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import styles from './ManageHappyFoxChat.module.css'

import { fetchBots } from 'Actions/bots'

import Link from 'Design/components/Link'
import FormControl from 'Design/components/FormControl'
import Select from 'Design/components/Select'
import Button from 'Design/components/Button'
import Heading from 'Design/components/Heading'
import Label from 'Design/components/Label'
import FormErrorText from 'Design/components/FormErrorText'
import ConfirmationModal from 'Design/components/ConfirmationModal'

import { useGetChatProfileQuery, useUninstallMutation, useUpdateBotProfileMutation } from './happyfox-chat.service'
import UnInstallApp from '../../../components/UnInstallApp'
import { useAppDetailContext } from '../../AppDetailContext'
import PageLoader from 'Layout/PageLoader'
import { selectProfileSchema } from './happyfox-chat-auth-form-validation-schema'

const ManageChatProfile = (props) => {
  const { settings } = props

  const [showUninstallModal, setShowUninstallModal] = useState(false)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { name } = useAppDetailContext()

  const { isLoading: profileLoading, data: chatProfile } = useGetChatProfileQuery()
  const [uninstall, uninstallResult] = useUninstallMutation()
  const [updateBot, updateBotResult] = useUpdateBotProfileMutation()

  const {
    register,
    watch,
    reset,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting }
  } = useForm({
    defaultValues: {
      profile: settings?.default_profile.id ?? ''
    },
    resolver: yupResolver(selectProfileSchema)
  })

  useEffect(() => {
    reset({ profile: settings?.default_profile.id })
  }, [reset, settings])

  const defaultProfileId = watch('profile')

  const handleFormSubmit = async (data) => {
    const payload = {
      profile_id: data.profile
    }

    const promise = updateBot(payload).unwrap()

    toast.promise(promise, {
      loading: 'Connecting happyfox chat',
      success: 'Happyfox chat connected successfully',
      error: 'Error connecting happyfox chat'
    })

    promise.then(reset({ profile: data.profile }), (reason) => console.error('error->', reason.data.message))

    dispatch(fetchBots())
  }

  const handleUninstall = async () => {
    const promise = uninstall().unwrap()
    toast.promise(promise, {
      loading: `Uninstalling ${name}`,
      success: `${name} integration uninstalled successfully`,
      error: `Unable to uninstall ${name}. Try again`
    })

    promise.then(handleCloseModal)
  }

  const handleChangeAuthClick = () => {
    navigate('auth')
  }

  const handleCloseModal = () => {
    setShowUninstallModal(false)
  }

  const handleOpenModal = () => {
    setShowUninstallModal(true)
  }

  const disableSubmitButton = isSubmitting || !isDirty || updateBotResult.isLoading
  const isUninstalling = uninstallResult.isLoading

  if (profileLoading) {
    return <PageLoader />
  }

  return (
    <Fragment>
      <ConfirmationModal
        isOpen={showUninstallModal}
        title='Uninstall Happyfox Chat'
        description='Are you sure to uninstall this app?'
        onCloseButtonClick={handleCloseModal}
        onConfirmButtonClick={handleUninstall}
        confirmButtonText='Yes'
        cancelButtonText='No'
        disableConfirmButton={isUninstalling}
      />
      <Heading level={2}>Manage Profile</Heading>
      <section>
        <Label className={styles.Label}>
          You have successfully connected your HappyFox Chat account
          <Link role='button' className={styles.ChangeButton} onClick={handleChangeAuthClick}>
            Change
          </Link>
        </Label>
      </section>

      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <FormControl>
          <Label>Select profile</Label>
          <Select {...register('profile', { shouldUnregister: true })} value={defaultProfileId.toString()}>
            <option value='' disabled>
              Select
            </option>
            {chatProfile?.map((profile, index) => {
              return (
                <option key={`${profile.name}__${profile.id}-${index}`} value={profile.id}>
                  {profile.name}
                </option>
              )
            })}
          </Select>
          {errors.profile && <FormErrorText>{errors.profile.message}</FormErrorText>}
        </FormControl>
        <Button variant='primary' type='submit' className={styles.SaveButton} disabled={disableSubmitButton}>
          Connect
        </Button>
      </form>

      <UnInstallApp onUninstall={handleOpenModal} isLoading={isUninstalling} />
    </Fragment>
  )
}

export default ManageChatProfile
